/**
 * フロアの元用途（§3.7）。
 * 上に行く理由を「数値が増える」ではなく「具体物が手に入る」にするための仕組み。
 *
 * 注意：ここで餌場や水場を上層に足してはいけない。
 * 餌場・水場が下層にしかないことが §2.7（上へ伸ばすほど下が破綻する）の土台なので、
 * 元用途の報酬は資材と、使えるようになる設置物に限る。
 */
import { fireDiscovery } from './discovery';
import { bfs } from './graph';
import { keyOf } from './grid';
import { MaterialBundle } from './materials';
import { BUILDING } from './state';
import { World, addDrop, graphFor } from './world';

export interface FloorDef {
  id: string;
  name: string;
  standRow: number;
  use: string;
  /** 初めて開通した時に見つかる拾い物。その場に落ちるので、拾うのはプレイヤー（§10.8） */
  found?: MaterialBundle;
  /** 開通すると作れるようになる物 */
  unlock?: string[];
  at?: { c: number; r: number };
}

export const FLOORS = BUILDING.floors as FloorDef[];

export function floorById(id: string): FloorDef | undefined {
  return FLOORS.find((f) => f.id === id);
}

export interface FloorOpened {
  floor: FloorDef;
  /** 拾い物が現れた場所 */
  at: number;
}

/**
 * 猫が実際に立てるようになったフロアを開通とみなす。
 * 判定は「のら猫が行けるか」ではなく「今いる猫の誰かが行けるか」。
 * プレイヤーが道を通したのに、たまたま居る猫の種類のせいで開通しない、
 * という分かりにくさを避けるため、種類ごとの到達をまとめて見る。
 */
export function checkFloorOpening(world: World): FloorOpened[] {
  const state = world.state;
  const opened: FloorOpened[] = [];

  const speciesInUse = new Set(state.cats.map((c) => c.species));
  if (speciesInUse.size === 0) return opened;

  for (const floor of FLOORS) {
    if (state.openedFloors.includes(floor.id)) continue;

    let reached = false;
    for (const sp of speciesInUse) {
      const graph = graphFor(world, sp);
      const starts = state.cats.filter((c) => c.species === sp).map((c) => c.node);
      const reach = bfs(graph, starts);
      for (const k of reach.keys()) {
        if (Math.floor(k / state.grid.cols) === floor.standRow) {
          reached = true;
          break;
        }
      }
      if (reached) break;
    }
    if (!reached) continue;

    state.openedFloors.push(floor.id);
    for (const id of floor.unlock ?? []) {
      if (!state.unlockedItems.includes(id)) state.unlockedItems.push(id);
    }
    const spot = floor.at ?? { c: 1, r: floor.standRow };
    const at = keyOf(state.grid, spot.c, spot.r);
    // 拾い物はその場に落ちる。在庫には勝手に入らない（§10.8）
    if (floor.found) addDrop(world, spot.c, spot.r, floor.found);
    // その階に初めて届いた時にだけ出る物（§10.18）
    const seen = fireDiscovery(state, { on: 'floor', floor: floor.id });
    if (seen) world.justFound.push(seen);
    // 拾った場所には痕跡を残す（絵として見える）
    if (at < state.traces.length) state.traces[at] += 3;
    opened.push({ floor, at });
    world.dirty = true;
  }
  return opened;
}
